import { Component, Inject, ViewChild } from '@angular/core';
import { FileDropzoneComponent } from 'src/app/core/components/file-dropzone/file-dropzone.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface DropFileDialogData {
  title: string;
  /** Trusted HTML only — rendered via [innerHTML]; sanitize at the call site if needed. */
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Comma-separated extensions/MIME wildcards, e.g. `.pdf,.md,image/*` */
  acceptedFileTypes?: string;
  maxFileSizeMB?: number;
}

export interface DropFileDialogResult {
  confirmed: boolean;
  file?: File;
}

/**
 * Builds the native `<input type="file" accept="...">` value.
 * Windows Explorer often hides `.md` unless MIME types are listed explicitly.
 */
function buildNativeFilePickerAccept(acceptedFileTypes: string, relaxed: boolean): string {
  if (relaxed) {
    return '*/*';
  }
  if (!acceptedFileTypes || acceptedFileTypes === '*') {
    return '*/*';
  }
  const parts = new Set(
    acceptedFileTypes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
  if (parts.has('.md') || parts.has('.markdown')) {
    parts.add('.md');
    parts.add('.markdown');
    parts.add('text/markdown');
    parts.add('text/plain');
    parts.add('text/x-markdown');
  }
  if (parts.has('.pdf')) {
    parts.add('application/pdf');
  }
  if (parts.has('.txt')) {
    parts.add('text/plain');
  }
  if (parts.has('.doc')) {
    parts.add('application/msword');
  }
  if (parts.has('.docx')) {
    parts.add('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
  }
  return Array.from(parts).join(',');
}

/** Drag-and-drop / browse file picker with optional type and size validation. */
@Component({
  selector: 'app-drop-file-dialog',
  standalone: true,
  imports: [
    FileDropzoneComponent,
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    MatIconModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      
      <div mat-dialog-content class="dialog-content">
        <div class="message-text" [innerHTML]="data.message"></div>
        
        <!-- The one upload zone of the application; this dialog keeps only what is its own:
             the chosen-file card and the escape hatch for pickers that hide .md files. -->
        <app-file-dropzone
          [accept]="nativePickerAccept"
          [acceptValidation]="data.acceptedFileTypes || ''"
          [maxSizeMb]="data.maxFileSizeMB || null"
          [hint]="fileTypesHint"
          (filesSelected)="onFilesSelected($event)">
        </app-file-dropzone>

        <button
          *ngIf="showMarkdownPickerHelp"
          mat-button
          type="button"
          class="browse-all-btn"
          (click)="enableRelaxedPicker($event)">
          Can't see your .md file? Browse all files
        </button>

        <div class="file-preview" *ngIf="selectedFile">
          <div class="file-icon-wrapper">
            <mat-icon class="file-icon">{{ getFileIcon() }}</mat-icon>
          </div>
          <div class="file-details">
            <p class="file-name">{{ selectedFile.name }}</p>
            <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
          </div>
          <button
            mat-icon-button
            color="warn"
            type="button"
            (click)="removeFile($event)"
            class="remove-btn">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        
        <div class="error-message" *ngIf="errorMessage">
          <mat-icon>error</mat-icon>
          {{ errorMessage }}
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          class="action-btn cancel-btn bg-warn"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button
          class="action-btn confirm-btn bg-primary"
          [disabled]="!selectedFile"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Upload' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 450px;
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      color: #2a3547;
      margin: 0;
      padding: 24px 24px 16px 24px;
      line-height: 1.4;
    }

    .dialog-content {
      padding: 0 24px 24px 24px !important;
      color: #5a6a85;
    }

    .message-text {
      font-size: 14px;
      line-height: 1.6;
      color: #5a6a85;
      margin-bottom: 20px;
    }

    /* Zone de Drop */
    .browse-all-btn {
      margin-top: 10px;
      font-size: 12px;
      text-transform: none;
    }

    /* Prévisualisation du fichier */
    .file-preview {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px;
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .file-icon-wrapper {
      flex-shrink: 0;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f0f4ff;
      border-radius: 8px;
    }

    .file-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: #5d87ff;
    }

    .file-details {
      flex: 1;
      text-align: left;
      overflow: hidden;
    }

    .file-name {
      font-size: 14px;
      font-weight: 500;
      color: #2a3547;
      margin: 0 0 4px 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-size {
      font-size: 12px;
      color: #6b7280;
      margin: 0;
    }

    .remove-btn {
      flex-shrink: 0;
    }

    /* Message d'erreur */
    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      padding: 12px;
      background-color: #fef2f2;
      border-left: 3px solid #ef4444;
      border-radius: 6px;
      color: #dc2626;
      font-size: 13px;
    }

    .error-message mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    /* Actions */
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 0 24px 24px 24px !important;
      margin: 0 !important;
    }

    .action-btn {
      min-width: 120px;
      padding: 8px 24px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      text-transform: none;
      letter-spacing: 0.5px;
      transition: all 0.3s ease;
    }

    .action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .bg-primary {
      background-color: #5d87ff !important;
      color: white !important;
    }

    .bg-primary:hover:not(:disabled) {
      background-color: #4570ea !important;
      box-shadow: 0 4px 12px rgba(93, 135, 255, 0.3);
    }

    .bg-warn {
      background-color: #ff6692 !important;
      color: white !important;
    }

    .bg-warn:hover {
      background-color: #ff4d7d !important;
      box-shadow: 0 4px 12px rgba(255, 102, 146, 0.3);
    }
  `]
})
export class DropFileDialogComponent {
  @ViewChild(FileDropzoneComponent) dropzone?: FileDropzoneComponent;

  /** When true, native picker shows all files; validation still applies on selection. */
  relaxedNativePicker = false;
  selectedFile: File | null = null;
  errorMessage = '';

  constructor(
    public dialogRef: MatDialogRef<DropFileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DropFileDialogData
  ) { }

  get nativePickerAccept(): string {
    return buildNativeFilePickerAccept(this.data.acceptedFileTypes || '*', this.relaxedNativePicker);
  }

  /** Shown only when Markdown is in the allow-list (Gemini doc upload). */
  get showMarkdownPickerHelp(): boolean {
    const raw = (this.data.acceptedFileTypes || '').toLowerCase();
    return raw !== '' && raw !== '*' && (raw.includes('.md') || raw.includes('.markdown'));
  }

  /**
   * Opens the OS picker with accept-all after Windows hides .md in the custom filter.
   * Only the PICKER is widened: acceptValidation keeps judging against the declared types.
   */
  enableRelaxedPicker(event: Event): void {
    event.stopPropagation();
    this.relaxedNativePicker = true;
    this.errorMessage = '';
    this.dropzone?.clearError();
    setTimeout(() => this.dropzone?.openFilePicker(), 0);
  }

  /** The zone has already judged the file against acceptValidation and the size ceiling. */
  onFilesSelected(files: File[]): void {
    this.errorMessage = '';
    this.selectedFile = files[0] ?? null;
  }


  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.errorMessage = '';
  }

  onConfirm(): void {
    if (this.selectedFile) {
      const result: DropFileDialogResult = {
        confirmed: true,
        file: this.selectedFile
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    const result: DropFileDialogResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  getFileIcon(): string {
    if (!this.selectedFile) return 'insert_drive_file';

    const extension = this.selectedFile.name.split('.').pop()?.toLowerCase();
    const type = this.selectedFile.type;

    // Images
    if (type.startsWith('image/')) return 'image';

    // Documents
    if (extension === 'pdf') return 'picture_as_pdf';
    if (['md', 'markdown'].includes(extension || '')) return 'article';
    if (['doc', 'docx'].includes(extension || '')) return 'description';
    if (['xls', 'xlsx'].includes(extension || '')) return 'table_chart';
    if (['ppt', 'pptx'].includes(extension || '')) return 'slideshow';

    // Archives
    if (['zip', 'rar', '7z'].includes(extension || '')) return 'folder_zip';

    // Code
    if (['js', 'ts', 'html', 'css', 'json', 'xml'].includes(extension || '')) return 'code';

    return 'insert_drive_file';
  }

  /** The formats line the zone prints under its Browse button. */
  get fileTypesHint(): string {
    const parts: string[] = [this.getFileTypesText()];
    if (this.data.maxFileSizeMB) {
      parts.push('Max size: ' + this.data.maxFileSizeMB + 'MB');
    }
    return parts.join(' • ');
  }

  getFileTypesText(): string {
    if (!this.data.acceptedFileTypes || this.data.acceptedFileTypes === '*') {
      return 'All file types accepted';
    }
    return `Accepted: ${this.data.acceptedFileTypes}`;
  }
}

